/* eslint-disable */
import { createRouter, createWebHistory } from "vue-router";
import Cookies from "js-cookie";
// Lazy-load Home page: non-critical until user navigates there
const HomePage = () => import('@/pages/HomePage.vue');
const NotFound = () => import('@/pages/NotFound.vue');
const AnalyticsPage = () => import('@/pages/AnalyticsPage.vue');
const QuizzesPage = () => import('@/pages/QuizzesPage.vue');
const ResourcesPage = () => import('@/pages/ResourcesPage.vue');
const ResourceDetail = () => import('@/pages/ResourceDetail.vue');
const ProfilePage = () => import('@/pages/ProfilePage.vue');
const QuizDetail = () => import('@/pages/QuizDetail.vue');
const QuizResultDetail = () => import('@/pages/QuizResultDetail.vue');
const GoogleAuthCallback = () => import('@/components/GoogleAuthCallback.vue');
const FacebookAuthCallback = () => import('@/components/FacebookAuthCallback.vue');
const QuizStartPage = () => import('@/pages/StartQuizPage.vue');
const AssignmentPage = () => import('@/pages/CreateAssignment.vue');
const CreateStudyGroup = () => import('@/pages/CreateStudyGroup.vue');
const GroupsList = () => import('@/pages/GroupsList.vue');
const GroupsViewAll = () => import('@/pages/GroupsViewAll.vue');
const GroupDetail = () => import('@/pages/GroupDetail.vue');
const BookmarksPage = () => import('@/pages/BookmarksPage.vue');
const ProfileSettings = () => import('@/pages/ProfileSettings.vue');
const DynamicPage = () => import('@/pages/DynamicPage.vue');
const IndividualPage = () => import('@/pages/IndividualPage.vue');

// Dashboard Pages
const DashboardPage = () => import('@/pages/dashboard/DashboardPage.vue');
const DashboardProfileSettings = () => import('@/pages/dashboard/DashboardProfileSettings.vue');
const QuizzesDashboardPage = () => import('@/pages/dashboard/QuizzesDashboardPage.vue');
const ResourcesDashboardPage = () => import('@/pages/dashboard/ResourcesDashboardPage.vue');
const AssignmentsPage = () => import('@/pages/dashboard/AssignmentsPage.vue');
const GoalsPage = () => import('@/pages/dashboard/GoalsPage.vue');
const AchievementsPage2 = () => import('@/pages/dashboard/AchievementsPage.vue');
const QuickQuizPage = () => import('@/pages/dashboard/QuickQuizPage.vue');
const FlashcardsPage = () => import('@/pages/dashboard/FlashcardsPage.vue');
const StudyGroupsPage = () => import('@/pages/dashboard/StudyGroupsPage.vue');
const ForumPage = () => import('@/pages/dashboard/ForumPage.vue');
const LeaderboardPage = () => import('@/pages/dashboard/LeaderboardPage.vue');
const AnalyticsDashboardPage = () => import('@/pages/dashboard/AnalyticsDashboardPage.vue');
const AchievementsPage = () => import('@/pages/AchievementsPage.vue');

const routes = [
  { path: "/", name: "Home", component: HomePage },
  { path: "/login", name: "Login", component: () => import('@/pages/LoginPage.vue') },
  { path: "/user", redirect: { name: "Login" } },
  { path: "/register", name: "Register", component: () => import('@/pages/TrialPage.vue') },
  { path: "/trial", redirect: { name: "Register" } },
  { path: "/sign-up", redirect: { name: "Register" } },
  

  // Lazy-load contact & analytics & listing pages to reduce initial bundle
  { path: "/contact", name: "Contact", component: () => import('@/pages/ContactPage.vue') },
  { path: "/analytics", name: "Analytics", component: AnalyticsPage },
  { path: "/quiz", name: "Quizzes", component: QuizzesPage },
  { path: "/faq", name: "FAQ", component: () => import('@/pages/faqPage.vue') },
  // Forgot password page
  { path: "/forgetpassword", name: "ForgotPassword", component: () => import('@/pages/ForgotPasswordPage.vue') },
  // Reset password page (accepts token & email via query string)
  { path: "/reset-password", name: "ResetPassword", component: () => import('@/pages/ResetPasswordPage.vue') },

  {
    path: "/individual-learning-features",
    name: "IndividualLearningFeatures",
    // Lazy-load individual learning features page (non-critical)
    component: () => import('@/pages/IndividualPage.vue'),
  },
  {
    path: "/schools-groups-features",
    name: "SchoolsGroupsFeatures",
    component: () => import("@/pages/schoolsgroupsPage.vue"),
  },
  {
    path: "/quiz/:classAlias",
    name: "QuizzesByClass",
    component: QuizzesPage,
    props: true,
  },
  {
    path: "/quiz/:classAlias/:subjectAlias",
    name: "QuizzesByFilter",
    component: QuizzesPage,
    props: true,
  },
  {
    path: "/quiz/:classAlias/:subjectAlias/:quizAlias",
    name: "QuizDetail",
    // Lazy-load quiz detail only when requested
    component: QuizDetail,
    props: true,
  },

  { path: "/control-panel/teacher/class/:classId/:sectionId", name: "TeacherClassDetail", component: () => import('@/pages/control-panel/teacher/TeacherClassDetail.vue'), props: true, meta: { requiresTeacherApproval: true } },
  { path: "/learn", name: "Resources", component: ResourcesPage },
  {
    path: "/learn/:classAlias",
    name: "ResourcesByClass",
    component: ResourcesPage,
    props: true,
  },
  {
    path: "/learn/:classAlias/:subjectAlias",
    name: "ResourcesByFilter",
    component: ResourcesPage,
    props: true,
  },
  {
    path: "/learn/:class/:subject/:resourceAlias",
    name: "ResourceDetail",
    component: ResourceDetail,
    props: true,
  },

  {
    path: "/quiz/start/:classAlias/:subjectAlias/:quizAlias/:quizId",
    name: "QuizStart",
    // Lazy-load quiz start flow only for users who start a quiz
    component: QuizStartPage,
    meta: { title: "Quiz", requiresAuth: true },
    beforeEnter: (to, from, next) => {
      const authToken = Cookies.get("authToken");
      if (!authToken) {
        next({ name: "Login", query: { redirect: to.fullPath } });
      } else {
        next();
      }
    },
  },

  {
    path: "/quiz/start/:classAlias/:subjectAlias/:quizAlias/:quizId/:attemptId",
    name: "QuizStartFallback",
    // Fallback route for backward compatibility
    component: QuizStartPage,
    meta: { title: "Quiz", requiresAuth: true },
    beforeEnter: (to, from, next) => {
      const authToken = Cookies.get("authToken");
      if (!authToken) {
        next({ name: "Login", query: { redirect: to.fullPath } });
      } else {
        next();
      }
    },
  },

  // Dashboard Routes
  {
    path: "/dashboard",
    name: "dashboard",
    // Dashboard pages are behind auth; lazy-load to keep initial app light
    component: DashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/settings",
    name: "DashboardProfileSettings",
    component: DashboardProfileSettings,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/quizzes",
    name: "QuizzesDashboardPage",
    component: QuizzesDashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/resources",
    name: "ResourcesDashboardPage",
    component: ResourcesDashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/analytics",
    name: "AnalyticsDashboardPage",
    component: AnalyticsDashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/follow-requests",
    name: "FollowRequests",
    component: () => import('@/pages/dashboard/FollowRequestsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: "/assignments",
    name: "assignments",
    component: AssignmentsPage,
    meta: { requiresAuth: true },
  },
 
  {
    path: "/dashboard/resources",
    name: "ResourcesDashboardPageources",
    component: ResourcesDashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/goals",
    name: "goals",
    component: GoalsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/achievements",
    name: "achievements",
    component: AchievementsPage2,
    meta: { requiresAuth: true },
  },
  {
    path: "/quick-quiz",
    name: "quick-quiz",
    component: QuickQuizPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/flashcards",
    name: "flashcards",
    component: FlashcardsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/study-groups",
    name: "study-groups",
    component: StudyGroupsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/forum",
    name: "forum",
    component: ForumPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/leaderboard",
    name: "leaderboard",
    component: LeaderboardPage,
    meta: { requiresAuth: true },
  },
  // Dashboard Pages
  // Assignmentpage page route
  {
    path: "/assignmentpage",
    name: "AssignmentPage",
    component: AssignmentPage,
  },
  

  {
    path: "/quiz/start/:quizId",
    redirect: (to) => {
      return {
        name: "QuizStart",
        params: {
          classAlias: to.params.classAlias || "",
          subjectAlias: to.params.subjectAlias || "",
          quizAlias: to.params.quizAlias || "",
          quizId: to.params.quizId,
        },
      };
    },
  },

  {
    path: "/create-group",
    name: "CreateStudyGroup",
    component: CreateStudyGroup,
  },

  {
    path: '/edit-group/:alias',
    name: 'EditStudyGroup',
    component: () => import('@/pages/CreateStudyGroup.vue'),
    meta: { requiresAuth: true }
  },

  {
    path: "/community",
    name: "GroupsList",
    component: GroupsList,
  },

  {
    path: "/communities",
    redirect: { name: "GroupsList" },
  },

  {
    path: "/community/:class?/:subject?",
    name: "GroupsListWithParams",
    component: GroupsList,
    props: true,
  },

  // {
  //   path: "/communities/:type?/:class?/:subject?",
  //   name: "GroupsViewAll",
  //   component: GroupsViewAll,
  //   props: (route) => ({
  //     type: route.params.type,
  //     class: route.params.class,
  //     subject: route.params.subject,
  //   }),
  // },

  {
    path: "/communities/:type?/:class?/:subject?",
    name: "GroupsViewAll",
    component: GroupsViewAll,
    beforeEnter: (to, from, next) => {
      const typeParam = to.params.type;
      const classParam = to.params.class;
      const subjectParam = to.params.subject;

      if (typeParam && !classParam && !subjectParam) {
        const validTypes = ["my-group", "recommended", "browse"];

        if (validTypes.includes(typeParam)) {
          return next();
        }
      }

      try {
        const typeParam = to.params.type;
        const classParam = to.params.class;
        const subjectParam = to.params.subject;

        if (
          typeParam &&
          classParam &&
          (typeof subjectParam === "undefined" || subjectParam === null)
        ) {
          const looksLikeClass =
            /^\d+(th|st|nd|rd)$/i.test(String(typeParam)) ||
            String(classParam).startsWith(String(typeParam) + "-");
          if (looksLikeClass) {
            const newParams = Object.assign({}, to.params);
            newParams.type = undefined;
            newParams.class = typeParam;
            newParams.subject = classParam;
            return next({ name: to.name, params: newParams, replace: true });
          }
        }

        if (typeParam && !classParam && !subjectParam) {
          const t = String(typeParam);
          const parts = t.split("-");
          const left = parts[0];
          if (/^\d+(th|st|nd|rd)$/i.test(left)) {
            const newParams = Object.assign({}, to.params);
            newParams.type = undefined;
            newParams.class = left;
            newParams.subject = parts.length > 1 ? t : undefined;
            return next({ name: to.name, params: newParams, replace: true });
          }
          if (/^\d+(th|st|nd|rd)$/i.test(t)) {
            const newParams = Object.assign({}, to.params);
            newParams.type = undefined;
            newParams.class = t;
            return next({ name: to.name, params: newParams, replace: true });
          }
        }
      } catch {
        // ignore and continue
      }
      next();
    },
    props: (route) => {
      const raw = (route.path || "").replace(/^\/communities\/?/, "");
      const segs = raw.split("/").filter(Boolean);

      let out = { type: undefined, class: undefined, subject: undefined };

      if (segs.length === 0) return out;

      if (segs.length === 1) {
        const s = segs[0];
        const m = s.match(/^(\d+(?:th|st|nd|rd))(?:-(.+))?$/i);
        if (m) {
          out.class = m[1];
          out.subject = m[2] ? s : undefined;
        } else {
          // treat as named type (e.g. recommended) when it doesn't look like a class
          out.type = s;
        }
        return out;
      }

      out.class = segs[0];
      out.subject = segs[1];
      return out;
    },
  },

  {
    path: "/community/:alias",
    name: "GroupDetail",
    component: GroupDetail,
    props: true,
  },

  {
    path: "/profile-setting",
    name: "ProfileSettings",
    component: ProfileSettings,
    props: true,
  },

  // ✅ Achievements route (only once)
  {
    path: "/profile/:username/achievements",
    name: "UserAchievements",
    // Lazy-load achievements to avoid loading large profile assets upfront
    component: AchievementsPage,
    props: true,
  },

  {
    path: "/quiz/result/:attemptId",
    name: "QuizResult",
    component: QuizResultDetail,
    props: true,
  },

 // Update the Profile route to handle redirection
   {
    path: '/my-connections',
    name: 'ControlPanelMyConnections',
    component: () => import('@/pages/control-panel/MyConnectionPage.vue'),
    meta: { requiresAuth: true }
  },
  
  // OR if you want separate routes for each user type, add these:
  {
    path: '/control-panel/institute/my-connections',
    name: 'ControlPanelInstituteFollowRequests',
    component: () => import('@/pages/control-panel/MyConnectionPage.vue'),
    meta: { requiresAuth: true, role: 'institute', dashboard: '/control-panel/institute' }
  },
  {
    path: '/control-panel/teacher/my-connections',
    name: 'ControlPanelTeacherFollowRequests',
    component: () => import('@/pages/control-panel/MyConnectionPage.vue'),
    meta: { requiresAuth: true, role: 'teacher', dashboard: '/control-panel/teacher' }
  },
  {
    path: '/control-panel/parent/my-connections',
    name: 'ControlPanelParentFollowRequests',
    component: () => import('@/pages/control-panel/MyConnectionPage.vue'),
    meta: { requiresAuth: true, role: 'parent', dashboard: '/control-panel/parent' }
  },
{
  path: "/profile/:username",
  name: "Profile",
  component: () => import('@/pages/ProfilePage.vue'),
  props: true,
  beforeEnter: async (to, from, next) => {
    try {
      const username = to.params.username
      const response = await getUserProfile(username)

      if (response?.user?.user_type) {
        const userType = response.user.user_type

        if (userType === 2) {
          return next({ name: 'TeacherProfilePage', params: { username } })
        }
        if (userType === 3) {
          return next({ name: 'ParentProfilePage', params: { username } })
        }
        if (userType === 4) {
          return next({ name: 'SchoolProfilePage', params: { username } })
        }

        // user_type === 1 → Student → stay on ProfilePage
      }

      next()
    } catch (error) {
      console.error('Error fetching profile for redirection:', error)
      next()
    }
  },
},

  { path: "/bookmarks", name: "Bookmarks", component: BookmarksPage },
  // Auth callbacks are only used during social login flows; lazy-load them
  { path: "/gauth", name: "GoogleAuthCallback", component: GoogleAuthCallback },
  {
    path: "/fauth",
    name: "FacebookAuthCallback",
    component: FacebookAuthCallback,
  },

  /* Control Panel - Institute */
  { path: "/control-panel/institute", name: "ControlPanelInstitute", component: () => import('@/pages/control-panel/institute/InstituteDashboard.vue') },
  { path: "/control-panel/institute/periods-schedule", name: "ControlPanelInstitutePeriodsSchedule", component: () => import('@/pages/control-panel/institute/PeriodsSchedule.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/teachers", name: "ControlPanelInstituteTeachers", component: () => import('@/pages/control-panel/institute/InstituteTeachers.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/teacher/:teacherId", name: "ControlPanelInstituteTeacherDetail", component: () => import('@/pages/control-panel/institute/InstituteTeacherDetail.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/class/:classId", name: "ControlPanelInstituteClassDetail", component: () => import('@/pages/control-panel/institute/InstituteClassDetail.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/section/:sectionId", name: "ControlPanelInstituteSectionDetail", component: () => import('@/pages/control-panel/institute/InstituteSectionDetail.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/students", name: "ControlPanelInstituteStudents", component: () => import('@/pages/control-panel/institute/InstituteStudents.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/classes", name: "ControlPanelInstituteClasses", component: () => import('@/pages/control-panel/institute/InstituteClasses.vue'), meta: { requiresSchoolApproval: true } },
  // Teacher Syllabus Management route
  { path: "/control-panel/institute/teacher-syllabus", name: "ControlPanelInstituteTeacherSyllabus", component: () => import('@/pages/control-panel/institute/TeacherSyllabusManagement.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/bulk-syllabus", name: "ControlPanelInstituteBulkSyllabus", component: () => import('@/pages/control-panel/institute/BulkSyllabusManager.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/assign-subjects", name: "ControlPanelInstituteAssignSubjects", component: () => import('@/pages/control-panel/institute/AssignSubjects.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/exam-setup", name: "ControlPanelInstituteExamSetup", component: () => import('@/pages/control-panel/institute/ExamSetup.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/subjects", name: "ControlPanelInstituteSubjects", component: () => import('@/pages/control-panel/institute/InstituteSubjects.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/tests", name: "ControlPanelInstituteTests", component: () => import('@/pages/control-panel/institute/InstituteTests.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/reports", name: "ControlPanelInstituteReports", component: () => import('@/pages/control-panel/institute/InstituteReports.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/school-period-timetables", name: "ControlPanelInstituteSchoolPeriodTimetables", component: () => import('@/pages/control-panel/institute/InstituteSchoolPeriodTimetables.vue'), meta: { requiresSchoolApproval: true } },
  { path: "/control-panel/institute/profile", name: "ControlPanelInstituteProfile", component: () => import('@/pages/control-panel/institute/InstituteProfile.vue'), meta: { requiresSchoolApproval: true } },

  /* Control Panel - Teacher */
  { path: "/control-panel/teacher", name: "ControlPanelTeacher", component: () => import('@/pages/control-panel/teacher/TeacherDashboard.vue') },
  { path: "/control-panel/teacher/classes", name: "ControlPanelTeacherClasses", component: () => import('@/pages/control-panel/teacher/TeacherClasses.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/students", name: "ControlPanelTeacherStudents", component: () => import('@/pages/control-panel/teacher/TeacherStudents.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/tests", name: "ControlPanelTeacherTests", component: () => import('@/pages/control-panel/teacher/TeacherTests.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/assignments", name: "ControlPanelTeacherAssignments", component: () => import('@/pages/control-panel/teacher/TeacherAssignments.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/reports", name: "ControlPanelTeacherReports", component: () => import('@/pages/control-panel/teacher/TeacherReports.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/profile", name: "ControlPanelTeacherProfile", component: () => import('@/pages/control-panel/teacher/TeacherProfile.vue'), meta: { requiresTeacherApproval: true } },
  { path: "/control-panel/teacher/syllabus", name: "ControlPanelTeacherSyllabus", component: () => import('@/pages/control-panel/teacher/TeacherSyllabus.vue'), meta: { requiresTeacherApproval: true } },

  /* Control Panel - Parent */
  { path: "/control-panel/parent", name: "ControlPanelParent", component: () => import('@/pages/control-panel/parent/ParentDashboard.vue') },
  { path: "/control-panel/parent/children", name: "ControlPanelParentChildren", component: () => import('@/pages/control-panel/parent/ParentChildren.vue') },
  { path: "/control-panel/parent/tests-assignments", name: "ControlPanelParentTestsAssignments", component: () => import('@/pages/control-panel/parent/ParentTestsAssignments.vue') },
  { path: "/control-panel/parent/reports", name: "ControlPanelParentReports", component: () => import('@/pages/control-panel/parent/ParentReports.vue') },
  { path: "/control-panel/parent/settings", name: "ControlPanelParentSettings", component: () => import('@/pages/control-panel/parent/ParentSettings.vue') },

  {
    path: "/:pathMatch(.*)*",
    name: "DynamicPage",
    // Dynamic catch-all page: load only when unknown route visited
    component: DynamicPage,
    props: (route) => ({
      slug: Array.isArray(route.params.pathMatch)
        ? route.params.pathMatch.join("/")
        : route.params.pathMatch,
    }),
  },
  { path: "/404", name: "NotFound", component: NotFound }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ left: 0, top: 0, behavior: "auto" });
      }, 100);
    });
  },
});

// Helper to determine whether the user is authenticated.
// It checks cookies and common localStorage keys used by the app.
function isAuthenticated() {
  try {
    const tokenFromCookie = Cookies.get("authToken");
    const tokenFromLS =
      localStorage.getItem("auth_token") || localStorage.getItem("authToken");
    const tokenFromSession = sessionStorage.getItem("authToken");
    return !!(tokenFromCookie || tokenFromLS || tokenFromSession);
  } catch (e) {
    return false;
  }
}

// Helper to get school_approved status
function isSchoolApproved() {
  try {
    let user = null;
    const raw = localStorage.getItem('user');
    if (raw) user = JSON.parse(raw);
    else {
      const userCookie = Cookies.get('user');
      if (userCookie) user = JSON.parse(decodeURIComponent(userCookie));
    }
    return user && user.school_approved === true;
  } catch (e) {
    return false;
  }
}

// Helper to get teacher_approved status
function isTeacherApproved() {
  try {
    let user = null;
    const raw = localStorage.getItem('user');
    if (raw) user = JSON.parse(raw);
    else {
      const userCookie = Cookies.get('user');
      if (userCookie) user = JSON.parse(decodeURIComponent(userCookie));
    }
    return user && user.teacher_approved === true;
  } catch (e) {
    return false;
  }
}

// Helper to check if teacher_approved exists in storage
function hasTeacherApprovalValue() {
  try {
    let user = null;
    const raw = localStorage.getItem('user');
    if (raw) user = JSON.parse(raw);
    else {
      const userCookie = Cookies.get('user');
      if (userCookie) user = JSON.parse(decodeURIComponent(userCookie));
    }
    return user && 'teacher_approved' in user;
  } catch (e) {
    return false;
  }
}

// Global guard: routes with `meta.requiresAuth` require authentication.
// If not authenticated, redirect to Home page.
router.beforeEach((to, from, next) => {
  // Redirect logged-in users away from login/register pages to home
  if ((to.name === "Login" || to.name === "Register") && isAuthenticated()) {
    return next({ name: "Home" });
  }

  // Check if route requires school approval (institute users only)
  if (to.matched.some((record) => record.meta && record.meta.requiresSchoolApproval)) {
    if (!isSchoolApproved()) {
      // Redirect to institute dashboard if school not approved
      return next({ path: '/control-panel/institute' });
    }
  }

  // Check if route requires teacher approval
  if (to.matched.some((record) => record.meta && record.meta.requiresTeacherApproval)) {
    // Only block if teacher_approved value exists and is false
    if (hasTeacherApprovalValue() && !isTeacherApproved()) {
      // Redirect to teacher dashboard if teacher not approved
      return next({ path: '/control-panel/teacher' });
    }
  }

  // If user navigates to generic /dashboard, redirect to role-specific control panel when possible
  if (to.path === '/dashboard' && isAuthenticated()) {
    let user = null;
    try {
      const raw = localStorage.getItem('user');
      if (raw) user = JSON.parse(raw);
      else {
        const userCookie = Cookies.get('user');
        if (userCookie) user = JSON.parse(decodeURIComponent(userCookie));
      }
    } catch (e) {
      user = null;
    }

    if (user && user.user_type) {
      const numeric = Number(user.user_type);
      let target = '/dashboard';
      if (Number.isFinite(numeric)) {
        if (numeric === 2) target = '/control-panel/teacher';
        else if (numeric === 3) target = '/control-panel/parent';
        else if (numeric === 4) target = '/control-panel/institute';
      } else if (typeof user.user_type === 'string') {
        const t = user.user_type.trim().toLowerCase();
        if (t === 'teacher') target = '/control-panel/teacher';
        else if (t === 'parent') target = '/control-panel/parent';
        else if (t === 'institution' || t === 'institute') target = '/control-panel/institute';
      }

      if (target !== to.path) {
        return next({ path: target });
      }
    }
  }

  if (to.matched.some((record) => record.meta && record.meta.requiresAuth)) {
    if (isAuthenticated()) {
      return next();
    }
    return next({ name: "Home" });
  }
  return next();
});

import { applySEO } from "@/seo/seo.helper";
router.afterEach((to) => {
  applySEO(to);
});

export default router;
